import { create } from "zustand";
import api from "../api/axios";

const STORAGE_KEY = "pms_bootstrap";

const saveCache = (data) => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      ts: Date.now(),
      payload: data,
    })
  );
};

const loadCache = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const { payload, ts } = JSON.parse(raw);
    return { payload, ts };
  } catch (e) {
    return null;
  }
};

export const usePmsStore = create((set, get) => ({
  guests: [],
  rooms: [],
  roomTypes: [],
  facilities: [],
  taxes: [],

  // load all APIs in parallel
  loadAllApis: async () => {
    const [guestsRes, roomsRes, roomTypesRes, facilitiesRes, taxesRes] =
      await Promise.all([
        api.get("pms/guests"),
        api.get("pms/rooms"),
        api.get("pms/room-type"),
        api.get("pms/rate-types"),
        // api.get("pms/facilities"),
        api.get("pms/taxes"),
      ]);

    const data = {
      guests: guestsRes.data,
      rooms: roomsRes.data,
      room_types: roomTypesRes.data,
      facilities: facilitiesRes.data,
      taxes: taxesRes.data,
    };

    saveCache(data);

    set({
      guests: data.guests,
      rooms: data.rooms,
      roomTypes: data.room_types,
      facilities: data.facilities,
      taxes: data.taxes,
    });

    return data;
  },

  // load from local cache (no network)
  loadFromCache: () => {
    const cached = loadCache();
    if (!cached) return null;
    const data = cached.payload;
    set({
      guests: data.guests || [],
      rooms: data.rooms || [],
      roomTypes: data.room_types || [],
      facilities: data.facilities || [],
      taxes: data.taxes || [],
    });
    return cached;
  },

  addGuest: (guest) => {
    set((state) => {
      const updated = [...state.guests, guest];
      const c = loadCache() || { payload: {} };
      c.payload.guests = updated;
      saveCache(c.payload);
      return { guests: updated };
    });
  },

  updateGuest: (guest) => {
    set((state) => {
      const updated = state.guests.map((g) => (g.id === guest.id ? guest : g));
      const c = loadCache() || { payload: {} };
      c.payload.guests = updated;
      saveCache(c.payload);
      return { guests: updated };
    });
  },

  deleteGuest: (id) => {
    set((state) => {
      const filtered = state.guests.filter((g) => g.id !== id);
      const c = loadCache() || { payload: {} };
      c.payload.guests = filtered;
      saveCache(c.payload);
      return { guests: filtered };
    });
  },

  // room helpers (same pattern)
  addRoom: (room) => {
    set((state) => {
      const updated = [...state.rooms, room];
      const c = loadCache() || { payload: {} };
      c.payload.rooms = updated;
      saveCache(c.payload);
      return { rooms: updated };
    });
  },

  updateRoom: (room) => {
    set((state) => {
      const updated = state.rooms.map((r) => (r.id === room.id ? room : r));
      const c = loadCache() || { payload: {} };
      c.payload.rooms = updated;
      saveCache(c.payload);
      return { rooms: updated };
    });
  },

  deleteRoom: (id) => {
    set((state) => {
      const filtered = state.rooms.filter((r) => r.id !== id);
      const c = loadCache() || { payload: {} };
      c.payload.rooms = filtered;
      saveCache(c.payload);
      return { rooms: filtered };
    });
  },

  // clear on logout
  clearPms: () => {
    localStorage.removeItem(STORAGE_KEY);
    set({
      guests: [],
      rooms: [],
      roomTypes: [],
      facilities: [],
      taxes: [],
    });
  },
}));
